import type { GuestContactMethod } from '@repo/api-contracts';
import { type ReactElement, useEffect, useId, useRef, useState } from 'react';
import { Link } from 'react-router';
import { GuestQuoteLinkNotice } from '@/entities/quote';
import { STORE_PATH } from '@/shared/config';
import { Button, buttonVariants, cn, EyebrowLabel } from '@/shared/ui';
import type { GuestQuoteRequestReceipt } from '../model/use-submit-guest-quote-request';

export interface GuestQuoteRequestDoneProps {
  readonly receipt: GuestQuoteRequestReceipt;
}

/** 이메일 고객은 접수 확인 메일(GUEST_RECEIVED)이 가고, 휴대폰 고객은 운영자가 카카오톡으로 직접 연락한다(설계 G15·G12). */
const CONTACT_NOTES: Readonly<Record<GuestContactMethod, string>> = {
  EMAIL:
    '남겨 주신 이메일로 접수 확인 메일과 견적 링크를 보내 드렸어요. 메일이 보이지 않으면 스팸함도 확인해 주세요.',
  PHONE: '남겨 주신 휴대폰 번호로 담당자가 카카오톡 상담 연락을 드려요.',
};

type CopyState = 'idle' | 'copied' | 'failed';

const COPY_MESSAGES: Readonly<Record<CopyState, string>> = {
  idle: '',
  copied: '링크를 복사했어요.',
  failed: '복사하지 못했어요. 링크를 길게 눌러 직접 복사해 주세요.',
};

/**
 * 비회원 견적 요청 완료(설계 G12) — 접수번호, 회신 기한("영업일 기준 1일 이내"), 견적 링크(서명 링크 — 로그인 없이 열린다)와 복사.
 * 이 화면은 지금 기록 항목의 history state 로만 다시 열린다(새로고침·뒤로 가기). 이 화면 주소를 즐겨찾기하면 접수 결과가 없으므로
 * 견적 링크 자체를 열어 즐겨찾기하라고 권한다. 화면이 나타나면 초점을 제목으로 옮겨 화면낭독기가 접수됐음을 바로 읽게 하고,
 * 복사 결과는 조용히 알린다(status).
 */
export const GuestQuoteRequestDone = ({ receipt }: GuestQuoteRequestDoneProps): ReactElement => {
  const titleId = useId();
  const titleRef = useRef<HTMLHeadingElement>(null);
  const [copyState, setCopyState] = useState<CopyState>('idle');

  useEffect(() => {
    titleRef.current?.focus();
  }, []);
  const linkUrl = new URL(receipt.linkPath, window.location.origin).toString();

  const copyLink = async (): Promise<void> => {
    try {
      await navigator.clipboard.writeText(linkUrl);
      setCopyState('copied');
    } catch {
      setCopyState('failed');
    }
  };

  return (
    <section
      aria-labelledby={titleId}
      className="flex flex-col gap-10 border-b border-foreground/10 pb-16"
    >
      <div className="flex flex-col gap-4">
        <EyebrowLabel accent>Received</EyebrowLabel>
        <h2
          id={titleId}
          ref={titleRef}
          tabIndex={-1}
          className="text-[clamp(1.75rem,3vw,2.5rem)] leading-tight font-bold tracking-[-0.02em] outline-none"
        >
          상담 요청을 접수했어요
        </h2>
        <p className="text-base font-medium">영업일 기준 1일 이내 연락드립니다.</p>
        <p className="max-w-xl text-sm leading-relaxed text-muted-foreground">
          {CONTACT_NOTES[receipt.contactMethod]}
        </p>
      </div>

      <dl className="grid gap-x-8 gap-y-6 border-t border-foreground pt-6 sm:grid-cols-[8rem_minmax(0,1fr)]">
        <dt className="text-sm text-muted-foreground">접수번호</dt>
        <dd className="text-2xl font-bold tracking-[-0.01em] tabular-nums">
          {receipt.quoteNumber}
        </dd>

        <dt className="text-sm text-muted-foreground">견적 링크</dt>
        <dd className="flex min-w-0 flex-col gap-3">
          <Link to={receipt.linkPath} className="text-sm break-all underline underline-offset-4">
            {linkUrl}
          </Link>
          <div className="flex flex-wrap items-center gap-3">
            <Button
              variant="outline"
              className="h-11 border-foreground/20 px-6"
              onClick={() => {
                void copyLink();
              }}
            >
              링크 복사
            </Button>
            <p role="status" aria-live="polite" className="text-xs text-muted-foreground">
              {COPY_MESSAGES[copyState]}
            </p>
          </div>
          <p className="text-xs leading-relaxed text-muted-foreground">
            이 링크로 진행 상황을 확인할 수 있어요. 로그인 없이 열리니 위 견적 링크를 열어
            즐겨찾기에 저장해 두세요.
          </p>
          <GuestQuoteLinkNotice />
        </dd>
      </dl>

      <Link
        to={STORE_PATH}
        className={cn(
          buttonVariants({ variant: 'outline', size: 'lg' }),
          'h-11 w-fit border-foreground/20 px-6',
        )}
      >
        스토어 둘러보기
      </Link>
    </section>
  );
};
